// src/components/vault/ui/VaultSidebar.tsx
import { Check, Loader2, Lock } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Requirement, Status } from "../../../redux/interfaces/folder.types";
import { ScrollArea } from "../../ui/scroll-area";
import { AutoSaveIndicator } from "./AutoSaveIndicator";
import { FolderStatusBadge } from "./FolderStatusBadge";
import { useAutoSaveContextOptional } from "../../../contexts/AutoSaveContext";
import {
  getRequirementProcessingLabel,
  getRequirementGroup,
  getVaultRequirementLockedDescription,
  isBdcAuthorizationRequirement,
  isRequirementProcessing,
  isRequirementLockedByPrerequisites,
  isRequirementVisuallyComplete,
  isIdentityRequirement,
  sortRequirementsByGroup,
} from "../utils/requirementGrouping";
import {
  getIdentityValidationActivityLabel,
  isIdentityValidationActivityProcessing,
  type IdentityValidationActivity,
} from "../identityValidationActivity";

// ============================================================================
// Folio Header Component
// ============================================================================

interface FolioHeaderProps {
  folioNumber?: string;
  tenantName?: string;
  actorType?: string;
  folderStatus?: string;
}

function FolioHeader({
  folioNumber,
  tenantName,
  folderStatus,
}: FolioHeaderProps) {
  if (!folioNumber && !tenantName) return null;

  return (
    <div className="space-y-1">
      {/* Folio + status badge */}
      {folioNumber && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-semibold text-[#28397a] tracking-wide uppercase">
            Folio #{folioNumber}
          </span>
          <FolderStatusBadge status={folderStatus} />
        </div>
      )}
      {/* Name */}
      {tenantName && (
        <h2 className="text-base font-semibold text-slate-900 leading-tight">
          {tenantName}
        </h2>
      )}
    </div>
  );
}

// Descriptions for each requirement slug
const requirementDescriptions: Record<string, string> = {
  // Documentos de identificación
  "official-id": "Documento de identidad vigente",
  "identificacion-oficial": "Documento de identidad vigente",

  // Validacion de datos (virtual step)
  "data-validation": "Confirma los datos extraidos de tus documentos",

  // Comprobantes
  "proof-of-income": "Comprobantes de ingresos recientes",
  "comprobante-ingresos": "Comprobantes de ingresos recientes",
  "proof-of-address": "Comprobante de domicilio reciente",
  "comprobante-domicilio": "Comprobante de domicilio reciente",

  // Referencias
  "personal-reference-1": "Datos de contacto de referencia",
  "personal-reference-2": "Datos de contacto de referencia",
  "referencia-personal-1": "Datos de contacto de referencia",
  "referencia-personal-2": "Datos de contacto de referencia",
  "commercial-reference-1": "Datos de referencia comercial",
  "commercial-reference-2": "Datos de referencia comercial",
  "referencia-comercial-1": "Datos de referencia comercial",
  "referencia-comercial-2": "Datos de referencia comercial",

  // Firmas y autorizaciones
  "digital-signature": "Firma electrónica del documento",
  "firma-digital": "Firma electrónica del documento",
  "bdc-authorization": "Autorización de consulta crediticia",
  "autorizacion-bdc": "Autorización de consulta crediticia",
  "bdc-company-data": "Datos de la empresa para consulta crediticia",

  // Formularios de personas
  "tenant-form": "Datos personales del inquilino",
  "formulario-inquilino": "Datos personales del inquilino",
  "formulario-del-inquilino": "Datos personales del inquilino",
  "guarantor-form": "Datos del obligado solidario",
  "formulario-fiador": "Datos del obligado solidario",
  "formulario-obligado-solidario": "Datos del obligado solidario",
  "landlord-form": "Datos personales del propietario",
  "formulario-propietario": "Datos personales del propietario",
  "owner-representative-form": "Datos del representante",
  "formulario-representante-propietario": "Datos del representante",

  // Formularios de empresas
  "business-form": "Datos de la empresa",
  "formulario-empresa": "Datos de la empresa",
  "business-cja-form": "Datos de la empresa",
  "business-short-form": "Datos básicos de la empresa",
  "business-form-lease-contract": "Datos de la empresa arrendadora",
  "legal-rep-form": "Datos del representante legal",
  "formulario-representante-legal": "Datos del representante legal",
  "legal-rep-short-form": "Datos del representante legal",

  // Documentos de empresa
  "constitutive-act": "Acta constitutiva de la empresa",
  "acta-constitutiva": "Acta constitutiva de la empresa",
  "sat-compliance": "Opinión de cumplimiento fiscal",
  "opinion-sat": "Opinión de cumplimiento fiscal",
  "power-of-attorney": "Poder notarial vigente",
  "poder-legal": "Poder notarial vigente",

  // Documentos de inmueble
  predial: "Recibo de pago del predial vigente",
  "boleta-registral": "Boleta registral del inmueble",
  "escrituras-predial": "Escrituras o predial del inmueble",
  escrituras: "Escrituras del inmueble",

  // Contrato
  "lease-contract-form": "Datos de la renta y contrato",
  "formulario-contrato": "Datos de la renta y contrato",
};

// Fallback descriptions based on requirement name (for cases where slug doesn't match)
const nameDescriptions: Record<string, string> = {
  "Identificación Oficial": "Documento de identidad vigente",
  "Formulario del Inquilino": "Datos personales del inquilino",
  "Formulario del Fiador": "Datos del obligado solidario",
  "Formulario del Obligado Solidario": "Datos del obligado solidario",
  "Formulario del Propietario": "Datos personales del propietario",
  "Formulario de la Empresa": "Datos de la empresa",
  "Datos de la Empresa para Buró de Crédito":
    "Datos de la empresa para consulta crediticia",
  "Formulario del Representante Legal": "Datos del representante legal",
  "Comprobante de Ingresos": "Comprobantes de ingresos recientes",
  "Comprobante de Domicilio": "Comprobante de domicilio reciente",
  "Referencia Personal 1": "Datos de contacto de referencia",
  "Referencia Personal 2": "Datos de contacto de referencia",
  "Referencia Comercial 1": "Datos de referencia comercial",
  "Referencia Comercial 2": "Datos de referencia comercial",
  "Firma Digital": "Firma electrónica del documento",
  "Autorización BDC": "Autorización de consulta crediticia",
  "Acta Constitutiva": "Acta constitutiva de la empresa",
  "Opinión de Cumplimiento SAT": "Opinión de cumplimiento fiscal",
  "Poder del Representante Legal": "Poder notarial vigente",
  Predial: "Recibo de pago del predial vigente",
  "Boleta Registral": "Boleta registral del inmueble",
  "Escrituras o Predial": "Escrituras o predial del inmueble",
  "Contrato de Arrendamiento": "Datos de la renta y contrato",
};

interface VaultSidebarProps {
  title: string;
  description?: string;
  folioNumber?: string;
  tenantName?: string;
  actorType?: string;
  folderStatus?: string;
  requirements: Requirement[];
  activeRequirement: string | null;
  onSelectRequirement: (requirementId: string) => void;
  /** When true, steps after ID are unlocked. When false, they show lock icons. */
  isIDCompleted?: boolean;
  /** When true, proof of address is completed */
  isProofOfAddressCompleted?: boolean;
  /** When true, data validation (virtual step) is completed */
  isDataValidationCompleted?: boolean;
  isBdcDestinationReviewed?: boolean;
  existingBdcRunRequirementId?: string | null;
  legacyCompletedBdcRequirementId?: string | null;
  bdcReportReplacementRequirementId?: string | null;
  bdcAuthoritativeRequirementStatus?: string;
  /** When true, the user confirmed their address on the ID is updated */
  isAddressConfirmedUpdated?: boolean;
  identityValidationActivity?: IdentityValidationActivity;
  /** Slugs of the locked form requirement(s) (e.g., ["tenant-form", "formulario-inquilino"]) */
  lockedFormSlugs?: string[] | null;
  bdcPrerequisiteSlugs?: string[] | null;
}

export function VaultSidebar({
  title,
  description,
  folioNumber,
  tenantName,
  actorType,
  folderStatus,
  requirements,
  activeRequirement,
  onSelectRequirement,
  isIDCompleted = true,
  isProofOfAddressCompleted = false,
  isDataValidationCompleted = false,
  isBdcDestinationReviewed = true,
  existingBdcRunRequirementId = null,
  legacyCompletedBdcRequirementId = null,
  bdcReportReplacementRequirementId = null,
  bdcAuthoritativeRequirementStatus,
  isAddressConfirmedUpdated = false,
  identityValidationActivity = null,
  lockedFormSlugs = null,
  bdcPrerequisiteSlugs = null,
}: VaultSidebarProps) {
  const lockContext = {
    requirements,
    isIDCompleted,
    isProofOfAddressCompleted,
    isDataValidationCompleted,
    isBdcDestinationReviewed,
    existingBdcRunRequirementId,
    legacyCompletedBdcRequirementId,
    bdcReportReplacementRequirementId,
    bdcAuthoritativeRequirementStatus,
    isAddressConfirmedUpdated,
    lockedFormSlugs,
    bdcPrerequisiteSlugs,
  };
  const completedCount = requirements.filter(
    (requirement) =>
      !(identityValidationActivity && isIdentityRequirement(requirement)) &&
      isRequirementVisuallyComplete(requirement),
  ).length;
  const progressPercentage =
    requirements.length > 0
      ? Math.round((completedCount / requirements.length) * 100)
      : 0;

  // Split requirements into groups using the shared sort utility
  // This ensures sidebar order matches the navigation order in VaultPageRedesign
  const sorted = sortRequirementsByGroup(requirements, lockedFormSlugs);
  const hasBdcAuthorization = requirements.some((requirement) =>
    isBdcAuthorizationRequirement(requirement.slug, requirement.type),
  );
  const identityRequirements = sorted.filter(
    (r) =>
      getRequirementGroup(
        r.slug,
        r.type,
        lockedFormSlugs,
        hasBdcAuthorization,
      ) === "identity",
  );
  const additionalRequirements = sorted.filter(
    (r) =>
      getRequirementGroup(
        r.slug,
        r.type,
        lockedFormSlugs,
        hasBdcAuthorization,
      ) === "additional",
  );
  const hasIdentityGroup = identityRequirements.length > 0;
  const hasAdditionalGroup = additionalRequirements.length > 0;
  const hasBothGroups = hasIdentityGroup && hasAdditionalGroup;

  const isRequirementLocked = (requirement: Requirement): boolean =>
    isRequirementLockedByPrerequisites(requirement, lockContext);

  // Estado de auto-guardado desde el contexto
  const autoSaveContext = useAutoSaveContextOptional();
  const autoSaveState = autoSaveContext?.autoSaveState;
  const hasAutoSaveActivity =
    autoSaveState &&
    (autoSaveState.isSaving ||
      autoSaveState.hasPendingChanges ||
      autoSaveState.lastSaved ||
      autoSaveState.hasStoredData);

  return (
    <div className="flex flex-col h-full">
      {/* Header with title and progress */}
      <div className="px-6 pt-2 pb-6 space-y-5">
        {/* Folio and Name */}
        <FolioHeader
          folioNumber={folioNumber}
          tenantName={tenantName}
          actorType={actorType}
          folderStatus={folderStatus}
        />

        {/* Title and description */}
        <div className="space-y-1.5">
          <h1 className="text-lg font-semibold text-slate-900 leading-tight">
            {title}
          </h1>
          {description && (
            <p className="text-sm text-slate-500 leading-relaxed">
              {description}
            </p>
          )}
        </div>

        {/* Progress indicator */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Progreso</span>
            <span className="font-medium text-slate-700">
              {completedCount} de {requirements.length}
            </span>
          </div>
          <div className="h-1.5 bg-slate-200/80 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#28397a] rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-6 border-t border-slate-200/60" />

      {/* Requirements list - grouped stepper style */}
      <ScrollArea className="flex-1">
        <nav className="px-6 py-4">
          <div className="relative">
            {/* Identity verification group */}
            {hasIdentityGroup && (
              <>
                {hasBothGroups && (
                  <div className="pb-1.5 pt-0.5">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      Verificación de identidad
                    </span>
                  </div>
                )}
                {identityRequirements.map((requirement, groupIndex) => (
                  <SidebarRequirementItem
                    key={requirement.id}
                    requirement={requirement}
                    stepNumber={groupIndex + 1}
                    isActive={activeRequirement === requirement.id}
                    isLast={
                      !hasBothGroups &&
                      groupIndex === identityRequirements.length - 1
                    }
                    isLastInGroup={
                      groupIndex === identityRequirements.length - 1
                    }
                    isLocked={isRequirementLocked(requirement)}
                    lockedDescription={getVaultRequirementLockedDescription(
                      requirement,
                      lockContext,
                    )}
                    identityValidationActivity={identityValidationActivity}
                    onClick={() => onSelectRequirement(requirement.id)}
                  />
                ))}
              </>
            )}

            {/* Separator between groups */}
            {hasBothGroups && (
              <div className="my-3 border-t border-slate-200/60" />
            )}

            {/* Additional documents group */}
            {hasAdditionalGroup && (
              <>
                {hasBothGroups && (
                  <div className="pb-1.5 pt-0.5">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      Documentos adicionales
                    </span>
                  </div>
                )}
                {additionalRequirements.map((requirement, groupIndex) => (
                  <SidebarRequirementItem
                    key={requirement.id}
                    requirement={requirement}
                    stepNumber={groupIndex + 1}
                    isActive={activeRequirement === requirement.id}
                    isLast={groupIndex === additionalRequirements.length - 1}
                    isLastInGroup={
                      groupIndex === additionalRequirements.length - 1
                    }
                    isLocked={isRequirementLocked(requirement)}
                    lockedDescription={getVaultRequirementLockedDescription(
                      requirement,
                      lockContext,
                    )}
                    identityValidationActivity={identityValidationActivity}
                    onClick={() => onSelectRequirement(requirement.id)}
                  />
                ))}
              </>
            )}
          </div>
        </nav>
      </ScrollArea>

      {/* Footer - Auto-save status */}
      {hasAutoSaveActivity && (
        <div className="px-6 py-4 border-t border-slate-200/60">
          <AutoSaveIndicator state={autoSaveState} />
        </div>
      )}
    </div>
  );
}

interface SidebarRequirementItemProps {
  requirement: Requirement;
  stepNumber: number;
  isActive: boolean;
  isLast: boolean;
  isLastInGroup?: boolean;
  isLocked?: boolean;
  lockedDescription?: string;
  identityValidationActivity?: IdentityValidationActivity;
  onClick: () => void;
}

function SidebarRequirementItem({
  requirement,
  stepNumber,
  isActive,
  isLast,
  isLastInGroup = false,
  isLocked = false,
  lockedDescription = "Completa los pasos anteriores primero",
  identityValidationActivity = null,
  onClick,
}: SidebarRequirementItemProps) {
  const localIdentityActivity = isIdentityRequirement(requirement)
    ? identityValidationActivity
    : null;
  const isCompleted =
    localIdentityActivity === null &&
    isRequirementVisuallyComplete(requirement);
  const isProcessing =
    localIdentityActivity !== null
      ? isIdentityValidationActivityProcessing(localIdentityActivity)
      : isRequirementProcessing(requirement);
  const processingLabel =
    getIdentityValidationActivityLabel(localIdentityActivity) ??
    getRequirementProcessingLabel(requirement);
  const isInvalid = requirement.status === Status.Invalid;

  // Get description based on slug, type, or name (in that order of priority)
  const description =
    requirementDescriptions[requirement.slug] ||
    requirementDescriptions[requirement.type] ||
    nameDescriptions[requirement.name] ||
    null;

  const handleClick = () => {
    if (!isLocked) {
      onClick();
    }
  };

  return (
    <div className="relative">
      {/* Vertical connector line */}
      {!isLast && !isLastInGroup && (
        <div
          className={cn(
            "absolute left-[15px] top-9 w-0.5 h-[calc(100%-12px)]",
            isLocked
              ? "bg-slate-200"
              : isCompleted
                ? "bg-[#28397a]/20"
                : "bg-slate-200",
          )}
        />
      )}

      <button
        onClick={handleClick}
        disabled={isLocked}
        className={cn(
          "w-full flex items-start gap-3 py-2.5 text-left transition-all group",
          isLocked && "cursor-not-allowed opacity-60",
        )}
      >
        {/* Step indicator */}
        <div className="flex-shrink-0 relative">
          {isProcessing ? (
            <div
              className="w-8 h-8 rounded-full bg-[#28397a] flex items-center justify-center"
              role="status"
              aria-label={processingLabel ?? "Procesando requisito"}
            >
              <span className="inline-flex animate-spin">
                <Loader2 className="w-4 h-4 text-white" strokeWidth={2.5} />
              </span>
            </div>
          ) : isCompleted ? (
            <div className="w-8 h-8 rounded-full bg-[#28397a] flex items-center justify-center transition-all duration-200">
              <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
              {isLocked && (
                <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                  <Lock className="h-2.5 w-2.5 text-slate-500" />
                </span>
              )}
            </div>
          ) : isLocked ? (
            <div className="w-8 h-8 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center transition-all duration-200">
              <Lock className="w-4 h-4 text-slate-400" strokeWidth={2} />
            </div>
          ) : isActive ? (
            <div className="w-8 h-8 rounded-full bg-[#28397a] flex items-center justify-center transition-all duration-200">
              <span className="text-sm font-semibold text-white">
                {stepNumber}
              </span>
            </div>
          ) : isInvalid ? (
            <div className="w-8 h-8 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center transition-all duration-200">
              <span className="text-sm font-medium text-red-500">
                {stepNumber}
              </span>
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center group-hover:border-slate-300 transition-all duration-200">
              <span className="text-sm font-medium text-slate-400 group-hover:text-slate-500">
                {stepNumber}
              </span>
            </div>
          )}
        </div>

        {/* Text content - title and description */}
        <div className="flex-1 min-w-0 pt-0.5">
          <span
            className={cn(
              "text-sm leading-tight block transition-colors duration-200",
              isLocked
                ? "text-slate-400"
                : isActive || isProcessing
                  ? "text-[#28397a] font-semibold"
                  : isCompleted
                    ? "text-slate-700"
                    : isInvalid
                      ? "text-red-700"
                      : "text-slate-600 group-hover:text-slate-800",
            )}
          >
            {requirement.name}
          </span>

          {/* Description - show for all, but different text for special states */}
          {isProcessing ? (
            <span className="text-xs text-[#28397a]/70 mt-0.5 block">
              {processingLabel}
            </span>
          ) : isLocked ? (
            <span className="text-xs text-slate-400 mt-0.5 block">
              {lockedDescription}
            </span>
          ) : isInvalid ? (
            <span className="text-xs text-red-500 mt-0.5 block">
              Requiere corrección
            </span>
          ) : description ? (
            <span className="text-xs text-slate-400 mt-0.5 block">
              {description}
            </span>
          ) : null}
        </div>
      </button>
    </div>
  );
}

// Compact version for mobile bottom sheet
export function VaultSidebarCompact({
  requirements,
  activeRequirement,
  onSelectRequirement,
}: Pick<
  VaultSidebarProps,
  "requirements" | "activeRequirement" | "onSelectRequirement"
>) {
  return (
    <div className="flex gap-3 p-4 overflow-x-auto">
      {requirements.map((requirement) => {
        const isActive = activeRequirement === requirement.id;
        const isCompleted = isRequirementVisuallyComplete(requirement);
        const isProcessing = isRequirementProcessing(requirement);
        const processingLabel = getRequirementProcessingLabel(requirement);

        return (
          <button
            key={requirement.id}
            onClick={() => onSelectRequirement(requirement.id)}
            className={cn(
              "flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 transition-all duration-200 active:scale-95",
              isActive ? "ring-2 ring-slate-900 ring-offset-2" : "",
              isCompleted ? "bg-slate-900" : "border-2 border-slate-300",
            )}
          >
            {isProcessing ? (
              <span
                className="inline-flex animate-spin"
                role="status"
                aria-label={processingLabel ?? "Procesando requisito"}
              >
                <Loader2 className="w-4 h-4 text-[#28397a]" />
              </span>
            ) : (
              isCompleted && <Check className="w-4 h-4 text-white" />
            )}
          </button>
        );
      })}
    </div>
  );
}
